import { collectWrites } from './support-b2';
import { empty, strval, strtoupper, phpCopy, array_unique, array_values } from '../../php-compat';
import type { PortEnv, PhpRef } from '../../port-contract';
import { at, array, isArray, values, num, f, list, record } from './support-b2';
export interface Deps {
    ft_status_expiry_row_paused(row: unknown): boolean;
    ft_painted_wall_wind_sensitive_cloud(id: string): boolean;
    persistent_aoe_status_is_depth_bearing(row: unknown): boolean;
    ft_painted_wall_intersects_sphere(wall: unknown, center: unknown, r: unknown, contact?: PhpRef<unknown>): boolean;
    ft_shape_sight_segment_hit_3d(shape: unknown, a: unknown, b: unknown, contain: boolean): unknown;
    check_uuid(id: string): boolean;
    item_field(row: unknown, key: string): unknown;
    fetch_statuses(filter: unknown): unknown;
    ft_encounter_shape(encounter: unknown): unknown;
    remove_status_effect_status_id_all_connections_to_all_targets(target: PhpRef<unknown>, affected: PhpRef<unknown>, id: unknown, roll: string, encounter: PhpRef<unknown>, campaign: unknown): unknown;
}
type CloudContactDeps = Pick<Deps, 'ft_status_expiry_row_paused' | 'ft_painted_wall_wind_sensitive_cloud'
    | 'persistent_aoe_status_is_depth_bearing' | 'ft_painted_wall_intersects_sphere' | 'ft_shape_sight_segment_hit_3d'>;
/** PHP: ft_painted_wall_contacts_cloud (includes/functions/janus/encounter/painted_wall_clouds.php:14) */
export function ftPaintedWallContactsCloud(wall: unknown, status: unknown, shape: unknown = null, env: Pick<PortEnv<CloudContactDeps>, 'deps'>): boolean {
    const d = env.deps;
    if (!empty(at(status, 'status_deleted')) || empty(at(status, 'status_aoe_source')) || d.ft_status_expiry_row_paused(status))
        return false;
    const id = strtoupper(strval(at(status, 'status_origin_id') ?? at(status, 'status_item_id') ?? ''));
    if (!d.ft_painted_wall_wind_sensitive_cloud(id))
        return false;
    if (strtoupper(strval(at(status, 'status_aoe_type') ?? '')) !== '981032B8-4F0B-43C9-8D98-67C693E4DB42' || !d.persistent_aoe_status_is_depth_bearing(status))
        return false;
    const radius = num(at(status, 'status_aoe_distance') ?? 0);
    if (radius <= 0 || !Number.isFinite(radius))
        return false;
    const center = record({});
    for (const axis of ['x', 'y', 'z'])
        center.set(axis, f((num(at(status, 'status_origin_point_' + axis) ?? 0) + num(at(status, 'status_distance_point_' + axis) ?? 0)) / 2));
    if (!d.ft_painted_wall_intersects_sphere(wall, center, f(radius)))
        return false;
    if (shape !== null) {
        let visible = false;
        const points = values(at(wall, 'points'));
        for (let i = 1; i < points.length && !visible; i++) {
            const a = points[i - 1], b = points[i], n = Math.max(1, Math.ceil(Math.sqrt((num(at(a, 'x')) - num(at(b, 'x'))) ** 2 + (num(at(a, 'y')) - num(at(b, 'y'))) ** 2 + (num(at(a, 'z')) - num(at(b, 'z'))) ** 2) / .5));
            for (let j = 0; j < n; j++) {
                const part = array(phpCopy(wall));
                part.set('points', list([j / n, (j + 1) / n].map(t => record(Object.fromEntries(['x', 'y', 'z'].map(k => [k, f(num(at(a, k)) + (num(at(b, k)) - num(at(a, k))) * t)]))))));
                const contact = { value: null as unknown };
                if (d.ft_painted_wall_intersects_sphere(part, center, f(radius), contact) && d.ft_shape_sight_segment_hit_3d(shape, record({ x: at(center, 'x'), y: at(center, 'y'), z: f(num(at(center, 'z')) + .001) }), record({ x: at(contact.value, 'x'), y: at(contact.value, 'y'), z: f(num(at(contact.value, 'z')) + .001) }), false) === null) {
                    visible = true;
                    break;
                }
            }
        }
        if (!visible)
            return false;
    }
    return true;
}
/** PHP: ft_painted_wall_wind_dispersion_candidates (includes/functions/janus/encounter/painted_wall_clouds.php:52) */
export function ftPaintedWallWindDispersionCandidates(wall: unknown, statuses: unknown, shape: unknown = null, env: PortEnv<Deps>): unknown {
    const ids = [];
    for (const status of values(statuses)) {
        if (!isArray(status) || !env.deps.check_uuid(strval(at(status, 'status_id') ?? '')) || !env.deps.check_uuid(strval(at(status, 'status_connected_id') ?? '')))
            continue;
        if (ftPaintedWallContactsCloud(wall, status, shape, env))
            ids.push(at(status, 'status_id'));
    }
    return array_values(array_unique(list(ids)));
}
/** PHP: ft_painted_wall_disperse_existing_clouds (includes/functions/janus/encounter/painted_wall_clouds.php:96) */
export function ftPaintedWallDisperseExistingClouds(wall: unknown, encounter: PhpRef<unknown>, affected: PhpRef<unknown>, roll: string, campaign: unknown, env: PortEnv<Deps>): import('../../port-contract').PortWithWrites<null> | null {
    return collectWrites(env, env => {
        const d = env.deps, id = strval(at(encounter.value, 'encounters', 'encounter_id') ?? d.item_field(encounter.value, 'item_id') ?? '');
        if (!d.check_uuid(id))
            return null;
        const statuses = d.fetch_statuses(record({ status_target_id: id, status_deleted: 0, status_aoe_source: 1 })), shape = d.ft_encounter_shape(encounter.value);
        for (const status of values(ftPaintedWallWindDispersionCandidates(wall, statuses, shape, env)))
            d.remove_status_effect_status_id_all_connections_to_all_targets(encounter, affected, status, roll, encounter, campaign);
        return null;
    });
}
